import { apiFetch } from '../api/http';

export type ItemAiLookupRun = {
  run_id: string;
  job_id: string;
  item_id: string;
  requested_by: string | null;
  status: string;
  query: string | null;
  item_make: string | null;
  item_model: string | null;
  item_summary: string | null;
  result_json: Record<string, unknown>;
  proposed_update: Record<string, unknown>;
  sources: unknown[];
  error: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
};

export type ItemAiLookupReviewItem = {
  item_id: string;
  item_code?: string | null;
  name_internal?: string | null;
  external_description?: string | null;
  brand?: string | null;
  manufacturer_name?: string | null;
  model_number?: string | null;
  model_name?: string | null;
  mpn?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  weight?: number | null;
  documentation?: unknown;
  attributes?: unknown;
  ai_image_source_url?: string | null;
  ai_image_storage_path?: string | null;
  firmware_latest_version?: string | null;
  firmware_download_url?: string | null;
  firmware_release_date?: string | null;
  firmware_notes?: string | null;
  firmware_checked_at?: string | null;
  accessories?: Array<{
    accessory_id?: string | null;
    name?: string | null;
    quantity?: number | null;
    notes?: string | null;
  }>;
  cases?: Array<{
    case_id?: string | null;
    name?: string | null;
    manufacturer?: string | null;
    model_number?: string | null;
    quantity?: number | null;
    notes?: string | null;
  }>;
};

export type ItemAiProductImage = {
  title?: string | null;
  url?: string | null;
  source_url?: string | null;
  kind?: string | null;
  notes?: string | null;
};

export type ItemAiDocumentCandidate = {
  title?: string | null;
  url?: string | null;
  kind?: string | null;
  source_url?: string | null;
};

export type ItemAiLookupAttachmentImportResult = {
  ok: boolean;
  imported: Array<{ kind: string; storagePath: string; sourceUrl: string; sourcePageUrl?: string | null; external?: boolean }>;
  skipped?: Array<{ kind: string; url?: string | null; reason: string }>;
  errors: Array<{ kind: string; message: string }>;
};

export type ItemAiLookupFieldUpdate = {
  key: string;
  value: unknown;
};

export type ItemAiLookupApplyResult = {
  ok: boolean;
  run: ItemAiLookupRun;
  item: ItemAiLookupReviewItem | null;
  appliedKeys: string[];
  rejectedKeys: string[];
  warnings?: Array<{
    kind: string;
    code?: string;
    message: string;
    url?: string | null;
    preservedAsLink?: boolean;
  }>;
};

export const ITEM_AI_LOOKUP_APPLIED_EVENT = 'venue:item-ai-lookup-applied';

export type ItemAiLookupAppliedEventDetail = {
  itemId: string;
  runId: string;
  item: ItemAiLookupReviewItem | null;
  appliedKeys: string[];
};

export function dispatchItemAiLookupApplied(detail: ItemAiLookupAppliedEventDetail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<ItemAiLookupAppliedEventDetail>(ITEM_AI_LOOKUP_APPLIED_EVENT, { detail }));
}

export function buildItemAiProposedUpdate(suggestion: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!suggestion || typeof suggestion !== 'object') return {};
  const patch: Record<string, unknown> = {};
  const scalarFields = [
    'external_description',
    'brand',
    'manufacturer_name',
    'model_number',
    'model_name',
    'mpn',
  ];
  scalarFields.forEach((field) => {
    const value = suggestion[field];
    if (value == null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    patch[field] = value;
  });
  const externalDescription = suggestion.external_description;
  const summary = suggestion.summary;
  if (
    (externalDescription == null || (typeof externalDescription === 'string' && !externalDescription.trim()))
    && summary != null
    && (typeof summary !== 'string' || summary.trim())
  ) {
    patch.external_description = summary;
  }
  const numberFieldMap: Record<string, string> = {
    length_in: 'length',
    width_in: 'width',
    height_in: 'height',
    weight_lb: 'weight',
  };
  Object.entries(numberFieldMap).forEach(([source, target]) => {
    const value = suggestion[source];
    if (typeof value === 'number' && Number.isFinite(value)) patch[target] = value;
    if (typeof value === 'string' && value.trim()) patch[target] = value;
  });
  [
    'signal_inputs',
    'signal_outputs',
    'signal_bidirectional',
    'power_requirements',
    'control_protocols',
    'mounting_notes',
    'rigging_notes',
    'attributes',
    'accessories',
    'cases',
    'documentation',
    'manual',
    'product_image',
    'firmware',
  ].forEach((field) => {
    const value = suggestion[field];
    if (value == null) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;
    patch[field] = value;
  });
  return patch;
}

async function handleJson<T>(response: Response): Promise<T> {
  const raw = await response.text();
  if (!response.ok) {
    let message = raw || `Request failed (${response.status})`;
    try {
      const parsed = raw ? JSON.parse(raw) as { error?: unknown; message?: unknown } : null;
      const candidate = parsed?.error ?? parsed?.message;
      if (candidate != null) message = String(candidate);
    } catch {
      // keep raw text
    }
    throw new Error(message);
  }
  return (raw ? JSON.parse(raw) : {}) as T;
}

export async function recordItemAiLookupRun(payload: {
  jobId: string;
  itemId: string;
  status: string;
  query?: string | null;
  itemMake?: string | null;
  itemModel?: string | null;
  itemSummary?: string | null;
  result?: Record<string, unknown> | null;
  proposedUpdate?: Record<string, unknown> | null;
  sources?: unknown[] | null;
  error?: string | null;
}): Promise<ItemAiLookupRun> {
  const response = await apiFetch('/item-ai-lookup/runs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const body = await handleJson<{ run: ItemAiLookupRun }>(response);
  return body.run;
}

export async function fetchItemAiLookupRun(runId: string): Promise<{
  run: ItemAiLookupRun;
  item: ItemAiLookupReviewItem | null;
}> {
  const response = await apiFetch(`/item-ai-lookup/runs/${encodeURIComponent(runId)}`);
  return handleJson<{ run: ItemAiLookupRun; item: ItemAiLookupReviewItem | null }>(response);
}

export async function applyItemAiLookupReview(payload: {
  runId: string;
  updates: ItemAiLookupFieldUpdate[];
  rejectedKeys?: string[];
}): Promise<ItemAiLookupApplyResult> {
  const response = await apiFetch(`/item-ai-lookup/runs/${encodeURIComponent(payload.runId)}/apply`, {
    method: 'POST',
    body: JSON.stringify({
      updates: payload.updates,
      rejectedKeys: payload.rejectedKeys ?? [],
    }),
  });
  return handleJson<ItemAiLookupApplyResult>(response);
}

export async function importItemAiLookupAttachments(payload: {
  itemId: string;
  image?: ItemAiProductImage | null;
  manual?: ItemAiDocumentCandidate | null;
  documentation?: ItemAiDocumentCandidate[] | null;
}): Promise<ItemAiLookupAttachmentImportResult> {
  const response = await apiFetch(`/item-ai-lookup/items/${encodeURIComponent(payload.itemId)}/attachments/import`, {
    method: 'POST',
    body: JSON.stringify({
      image: payload.image ?? null,
      manual: payload.manual ?? null,
      documentation: payload.documentation ?? null,
    }),
  });
  return handleJson<ItemAiLookupAttachmentImportResult>(response);
}
